// Implements: F-UIX-019, F-GEN-036
/**
 * One search of the provider catalogue, under `MODEL_CATALOGUE_KEY` plus the search itself, so
 * each search is cached apart and an answer for an older search is never shown for a newer one
 * (checklist 14); the previous list stays on screen while the next one is read. The same search
 * within five minutes is answered from the cache: the server keeps its own copy for as long.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import type { ApiError } from "../../../shared/api/ApiError";
import { MODEL_CATALOGUE_KEY } from "../../../shared/api/queryKeys";
import type { CatalogueView } from "../../../types/modelCatalogue";
import { fetchModelCatalogue } from "../api/modelCatalogueApi";

const CATALOGUE_STALE_MS = 5 * 60 * 1000;

export interface CatalogueSearch {
  readonly q: string;
  readonly reasoningOnly: boolean;
}

export function useModelCatalogue(search: CatalogueSearch): UseQueryResult<CatalogueView, ApiError> {
  const q = search.q.trim();
  return useQuery<CatalogueView, ApiError>({
    queryKey: [...MODEL_CATALOGUE_KEY, q, search.reasoningOnly],
    queryFn: ({ signal }) => fetchModelCatalogue({ q, reasoning_only: search.reasoningOnly }, signal),
    staleTime: CATALOGUE_STALE_MS,
    placeholderData: keepPreviousData,
    retry: false,
  });
}
